// Filtro de contrato do Contas a Receber — a FAMÍLIA como unidade.
//
// Pedido do Lucas (16/09/2026): *"preciso conseguir filtrar todos os contratos
// e aditivos juntos no filtro do contas a receber. fica difícil ficar vendo
// separadamente, além disso, tenho que poder selecionar mais de um contrato"*.
//
// Aditivo é uma linha FILHA em `operacao.contratos` (`contrato_pai_id`), e há
// aditivo DE aditivo em produção. O servidor resolve a raiz da árvore numa CTE
// recursiva e manda `contrato_raiz_*` em cada parcela — aqui só se agrupa.
//
// 🚨 Duas coisas que este arquivo existe para garantir:
//
//  1. A família aparece no filtro MESMO QUANDO O CONTRATO-PAI NÃO TEM PARCELA.
//     Não é hipótese: HY24023 (CORSAN Topografia) tem 27 aditivos com parcelas
//     e ZERO no pai — hoje o filtro mostra 27 linhas soltas e o número do pai
//     não existe em lugar nenhum. O mesmo vale para o HY26015 (Ceriluz).
//  2. O rótulo do "TUDO" carrega o NÚMERO do contrato. Com opções agrupadas o
//     antd só procura nas folhas: sem o número no rótulo, digitar "HY24023" na
//     busca não acharia a opção que junta a família.
//
// A régua é pura de propósito — o teste roda no CI (job dre-frontend), onde o
// jest do CRA5 não consegue carregar o antd v6.

/** O que a régua precisa de uma parcela. O resto da linha não interessa. */
export type ParcelaFamilia = {
  contrato_id?: string | null;
  contrato_numero?: string | null;
  contrato_titulo?: string | null;
  /** NULL quando a parcela já é do contrato-raiz (economia de payload). */
  contrato_raiz_id?: string | null;
  contrato_raiz_numero?: string | null;
  contrato_raiz_titulo?: string | null;
};

export type MembroFamilia = { id: string; numero: string; titulo: string };
export type Familia = {
  raizId: string;
  raizNumero: string;
  raizTitulo: string;
  /** Só os que têm parcela na lista carregada — é o que dá para filtrar. */
  membros: MembroFamilia[];
};

/** Um valor do filtro é o id de um contrato OU `fam:<id da raiz>`. */
export const PREFIXO_FAMILIA = 'fam:';

export const valorDaFamilia = (raizId: string) => PREFIXO_FAMILIA + raizId;

/** A raiz de uma parcela. Sem aditivo, o contrato é a própria raiz. */
export const raizDaParcela = (p: ParcelaFamilia): string =>
  p.contrato_raiz_id || p.contrato_id || '';

const rotulo = (numero: string, titulo: string) => numero + (titulo ? ` · ${titulo}` : '');

/** Agrupa as parcelas carregadas em famílias, ordenadas por número. */
export function montarFamilias(parcelas: ParcelaFamilia[]): Familia[] {
  const fam = new Map<string, { raizId: string; raizNumero: string; raizTitulo: string; membros: Map<string, MembroFamilia> }>();
  for (const p of parcelas) {
    if (!p.contrato_id) continue;          // parcela avulsa não entra no filtro de contrato
    const raizId = raizDaParcela(p);
    let f = fam.get(raizId);
    if (!f) {
      f = {
        raizId,
        // Quando a parcela é de um aditivo, o número/título da RAIZ vem do
        // servidor — é por isso que a família aparece com o pai sem parcela.
        raizNumero: p.contrato_raiz_numero || p.contrato_numero || '—',
        raizTitulo: p.contrato_raiz_titulo || p.contrato_titulo || '',
        membros: new Map(),
      };
      fam.set(raizId, f);
    }
    if (!f.membros.has(p.contrato_id)) {
      f.membros.set(p.contrato_id, {
        id: p.contrato_id,
        numero: p.contrato_numero || '—',
        titulo: p.contrato_titulo || '',
      });
    }
  }
  return Array.from(fam.values())
    .map(f => ({
      raizId: f.raizId,
      raizNumero: f.raizNumero,
      raizTitulo: f.raizTitulo,
      membros: Array.from(f.membros.values())
        .sort((a, b) => a.numero.localeCompare(b.numero, 'pt-BR')),
    }))
    .sort((a, b) => a.raizNumero.localeCompare(b.raizNumero, 'pt-BR'));
}

export type OpcaoSimples = { value: string; label: string };
export type OpcaoGrupo = { label: string; options: OpcaoSimples[] };
export type OpcaoContrato = OpcaoSimples | OpcaoGrupo;

/**
 * Contrato sem aditivo vira uma linha simples; com aditivo vira um grupo que
 * começa pelo "TUDO" — que é o que tira o "ficar vendo separadamente".
 */
export function opcoesDoFiltro(familias: Familia[]): OpcaoContrato[] {
  return familias.map(f => {
    if (f.membros.length <= 1) {
      const m = f.membros[0];
      return { value: m.id, label: rotulo(m.numero, m.titulo) };
    }
    return {
      label: rotulo(f.raizNumero, f.raizTitulo),
      options: [
        {
          value: valorDaFamilia(f.raizId),
          // O número no rótulo NÃO é enfeite: é o que a busca do antd casa.
          label: `${f.raizNumero} · TUDO (contrato + aditivos, ${f.membros.length} no filtro)`,
        },
        ...f.membros.map(m => ({ value: m.id, label: rotulo(m.numero, m.titulo) })),
      ],
    };
  });
}

/** Quantos contratos dá para escolher ao todo (rótulo "Todos (N)"). */
export const totalDeContratos = (familias: Familia[]) =>
  familias.reduce((n, f) => n + f.membros.length, 0);

/** Separa a seleção em contratos soltos e famílias inteiras. */
export function separarSelecao(valores: string[]): { ids: Set<string>; raizes: Set<string> } {
  const ids = new Set<string>();
  const raizes = new Set<string>();
  for (const v of valores) {
    if (v.startsWith(PREFIXO_FAMILIA)) raizes.add(v.slice(PREFIXO_FAMILIA.length));
    else if (v) ids.add(v);
  }
  return { ids, raizes };
}

/** A parcela passa pelo filtro? Seleção vazia = passa tudo (quem chama decide). */
export function parcelaNoFiltro(
  p: ParcelaFamilia, ids: Set<string>, raizes: Set<string>,
): boolean {
  if (!ids.size && !raizes.size) return true;
  if (p.contrato_id && ids.has(p.contrato_id)) return true;
  const raiz = raizDaParcela(p);
  return !!raiz && raizes.has(raiz);
}

/**
 * Um contrato só (nem família, nem vários) — é a condição para reordenar as
 * parcelas por arrasto: com dois contratos na tela, a numeração seria de quem?
 */
export function contratoUnicoDaSelecao(valores: string[]): string {
  if (valores.length !== 1) return '';
  return valores[0].startsWith(PREFIXO_FAMILIA) ? '' : valores[0];
}

/**
 * A família do contrato escolhido, quando ela tem mais gente — é o que permite
 * oferecer "+ incluir os N aditivos" a quem chegou pelo link de um contrato só.
 */
export function familiaComMaisMembros(
  valores: string[], familias: Familia[],
): Familia | null {
  const unico = contratoUnicoDaSelecao(valores);
  if (!unico) return null;
  const f = familias.find(x => x.membros.some(m => m.id === unico));
  return f && f.membros.length > 1 ? f : null;
}
